import { videoTagWithConsent } from './videoConsent.js';
import { reportRuntimeEvent } from './runtimeTelemetry.js';
import { loadGooglePublisherSdk } from './googlePublisherSdk.js';
let sdkPromise = null;
const MIN_LINEAR_SECONDS = 5;
const MAX_LINEAR_SECONDS = 90;
const AD_TIMER_GRACE_MS = 5000;
const COUNTDOWN_INTERVAL_MS = 1000;

function withCachebuster(value) {
  const tag = String(value || '').trim();
  if (!tag) return '';
  const cachebuster = `${Date.now()}${Math.floor(Math.random() * 100000)}`;
  return tag.replace(/%%CACHEBUSTER%%/g, cachebuster);
}

export function loadImaSdk(src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js') {
  if (typeof window === 'undefined') return Promise.reject(new Error('IMA requires a browser'));
  if (window.google?.ima) return Promise.resolve(window.google.ima);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-watchvim-ima-sdk="true"]');
    if (existing) {
      existing.addEventListener('load', () => window.google?.ima ? resolve(window.google.ima) : reject(new Error('IMA SDK did not initialize')), { once: true });
      existing.addEventListener('error', () => reject(new Error('IMA SDK failed to load')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.watchvimImaSdk = 'true';
    script.onload = () => window.google?.ima ? resolve(window.google.ima) : reject(new Error('IMA SDK did not initialize'));
    script.onerror = () => reject(new Error('IMA SDK failed to load'));
    document.head.appendChild(script);
  }).catch((error) => {
    sdkPromise = null;
    throw error;
  });

  return sdkPromise;
}

function dimensions(container) {
  const rect = container?.getBoundingClientRect?.();
  const width = Math.max(1, Math.round(rect?.width || container?.clientWidth || 640));
  const height = Math.max(1, Math.round(rect?.height || container?.clientHeight || 360));
  return { width, height };
}

function adTagSource(value) {
  const tag = String(value || '').trim();
  if (!tag) return 'unknown';
  if (/\/api\/ad-campaigns\/vast(?:[/?]|$)/i.test(tag)) return 'watchvim-direct';
  if (/\/api\/ads\/house-vast(?:[/?]|$)/i.test(tag)) return 'watchvim-house';
  if (/(?:^|\.)doubleclick\.net|(?:^|\.)googlesyndication\.com|(?:^|\.)googleadservices\.com/i.test(tag)) return 'google-demand';
  return 'other';
}

function errorDetail(event) {
  try {
    const error = event?.getError?.();
    const inner = error?.getInnerError?.();
    return {
      code: Number(error?.getErrorCode?.()) || null,
      innerCode: Number(inner?.getErrorCode?.() ?? inner?.code) || null,
      message: String(error?.getMessage?.() || error?.toString?.() || 'IMA ad error'),
    };
  } catch (_) {
    return { code: null, message: 'IMA ad error' };
  }
}

function currentTitleId() {
  if (typeof window === 'undefined') return '';
  const match = String(window.location?.pathname || '').match(/^\/watch\/([^/?#]+)/i);
  if (!match?.[1]) return '';
  try { return decodeURIComponent(match[1]); } catch (_) { return match[1]; }
}


function muxPlayerForContainer(container) {
  const scope = container?.closest?.('.playback-stage') || container?.parentElement || document;
  return scope?.querySelector?.('mux-player') || null;
}

export function muxContentVideoForContainer(container) {
  const muxPlayer = muxPlayerForContainer(container);
  const nativeEl = muxPlayer?.media?.nativeEl || null;
  if (!nativeEl) return null;
  if (typeof HTMLVideoElement !== 'undefined' && nativeEl instanceof HTMLVideoElement) return nativeEl;
  return String(nativeEl?.tagName || '').toUpperCase() === 'VIDEO' ? nativeEl : null;
}

function imaContentBridgeVideo(container) {
  const existing = container?.querySelector?.('video[data-watchvim-ima-content-bridge="true"]');
  if (existing) return existing;
  if (typeof document === 'undefined' || !container?.appendChild) return null;
  const video = document.createElement('video');
  video.dataset.watchvimImaContentBridge = 'true';
  video.muted = true;
  video.playsInline = true;
  video.preload = 'none';
  video.setAttribute('playsinline', '');
  video.setAttribute('aria-hidden', 'true');
  video.style.position = 'absolute';
  video.style.width = '1px';
  video.style.height = '1px';
  video.style.left = '0';
  video.style.bottom = '0';
  video.style.opacity = '0';
  video.style.pointerEvents = 'none';
  video.style.zIndex = '-1';
  container.appendChild(video);
  return video;
}

async function waitForImaContentVideo(container, timeoutMs = 2500) {
  const startedAt = Date.now();
  let contentVideo = muxContentVideoForContainer(container);
  while (!contentVideo && Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    contentVideo = muxContentVideoForContainer(container);
  }
  if (contentVideo) return { video: contentVideo, bridge: false };

  // The preroll must not be cancelled merely because the content decoder has
  // already rejected the Mux manifest (for example a codec mismatch in one
  // browser). Google IMA only needs a content media element for lifecycle and
  // mobile playback coordination. A tiny, muted bridge video keeps ad delivery
  // independent from content decoding; PlaybackPage still owns the real Mux
  // player and can surface its content error after the ad break.
  const bridge = imaContentBridgeVideo(container);
  if (bridge) return { video: bridge, bridge: true };

  const error = Object.assign(new Error('WatchVIM AVOD could not provide Google IMA a content video element'), {
    code: 'WATCHVIM_IMA_CONTENT_VIDEO_UNAVAILABLE',
  });
  throw error;
}

export async function createImaController({
  container,
  sdkUrl,
  getConsent = () => ({}),
  getCurrentTime,
  pauseContent,
  onState,
} = {}) {
  if (!container) throw new Error('IMA ad container is required');
  // Start before the play gesture; GPT remains responsible for its published
  // privacy message and IMA reads the genuine consent signal automatically.
  const publisherReady = loadGooglePublisherSdk().catch(() => null);
  const ima = await loadImaSdk(sdkUrl);

  let contentVideo;
  let contentVideoIsBridge = false;
  try {
    const binding = await waitForImaContentVideo(container);
    contentVideo = binding.video;
    contentVideoIsBridge = binding.bridge;
    if (contentVideoIsBridge) {
      const detail = {
        phase: 'ready',
        event: 'content-video-bridge',
        code: 'WATCHVIM_IMA_CONTENT_BRIDGE',
        message: 'IMA content bridge active while Mux native video is unavailable',
        tagSource: 'watchvim-player',
        position: '',
        slotId: '',
      };
      try { onState?.(detail); } catch (_) {}
      reportRuntimeEvent(detail);
    }
  } catch (error) {
    const detail = {
      phase: 'error',
      event: 'content-video-unavailable',
      code: error?.code || 'WATCHVIM_IMA_CONTENT_VIDEO_UNAVAILABLE',
      message: error?.message || 'IMA content video is unavailable',
      tagSource: 'watchvim-player',
      position: '',
      slotId: '',
    };
    try { onState?.(detail); } catch (_) {}
    reportRuntimeEvent(detail);
    throw error;
  }

  const displayContainer = new ima.AdDisplayContainer(container, contentVideo);
  const adsLoader = new ima.AdsLoader(displayContainer);
  const playhead = {};
  Object.defineProperty(playhead, 'currentTime', {
    enumerable: true,
    configurable: false,
    get: () => Math.max(0, Number(contentVideoIsBridge ? getCurrentTime?.() : contentVideo?.currentTime) || 0),
  });

  let initialized = false;
  let destroyed = false;
  let manager = null;
  let pending = null;
  let requestSequence = 0;
  let lastHouseCompletion = null;
  let countdownTimer = null;

  function activeContext(detail = {}) {
    const active = pending;
    return {
      ...detail,
      tagSource: detail.tagSource ?? active?.tagSource ?? 'unknown',
      position: detail.position ?? active?.position ?? '',
      slotId: detail.slotId ?? active?.slotId ?? '',
      slotNumber: Number(detail.slotNumber ?? active?.slotNumber ?? 0) || 0,
      slotTotal: Number(detail.slotTotal ?? active?.slotTotal ?? 0) || 0,
      durationSec: Number.isFinite(Number(detail.durationSec ?? active?.durationSec))
        ? Number(detail.durationSec ?? active?.durationSec)
        : null,
    };
  }

  function emit(detail) {
    const enriched = activeContext(detail);
    try { onState?.(enriched); } catch (_) {}
    reportRuntimeEvent(enriched);
  }

  function clearCountdown() {
    if (countdownTimer) window.clearInterval(countdownTimer);
    countdownTimer = null;
  }

  function emitCountdown() {
    if (!pending || !manager || pending.settled) return;
    let remaining = NaN;
    try { remaining = Number(manager.getRemainingTime?.()); } catch (_) {}
    if (!Number.isFinite(remaining) || remaining < 0) remaining = Number(pending.durationSec);
    if (!Number.isFinite(remaining)) return;
    emit({
      phase: 'playing',
      event: 'progress',
      remainingSec: Math.max(0, Math.ceil(remaining)),
    });
  }

  function startCountdown() {
    clearCountdown();
    emitCountdown();
    countdownTimer = window.setInterval(emitCountdown, COUNTDOWN_INTERVAL_MS);
  }

  function destroyManager() {
    clearCountdown();
    if (!manager) return;
    try { manager.destroy(); } catch (_) {}
    manager = null;
  }

  function settle(kind, value) {
    const active = pending;
    if (!active || active.settled) return;
    active.settled = true;
    if (active.timer) window.clearTimeout(active.timer);
    if (active.deadlineTimer) window.clearTimeout(active.deadlineTimer);
    clearCountdown();

    let outcome = value;
    if (kind === 'resolve') {
      const started = active.started === true || value?.started === true;
      const completed = started && (active.completed === true || value?.completed === true);
      outcome = {
        attempted: true,
        started,
        completed,
        skipped: !started && value?.skipped === true,
        consumed: started || value?.consumed === true,
        code: value?.code || null,
        durationSec: Number.isFinite(Number(active.durationSec)) ? Number(active.durationSec) : null,
        source: active.tagSource,
        slotId: active.slotId,
        slotNumber: active.slotNumber,
        slotTotal: active.slotTotal,
        ...value,
        started,
        completed,
        skipped: !started && value?.skipped === true,
        consumed: started || value?.consumed === true,
      };
    }

    if (kind === 'resolve' && active.tagSource === 'watchvim-house' && outcome?.started === true) {
      lastHouseCompletion = { position: active.position, slotId: active.slotId, at: Date.now() };
    }
    pending = null;
    // Every slot tears down its AdsManager before the caller advances to the
    // next slot or restores the existing Mux content player.
    destroyManager();
    if (kind === 'resolve') active.resolve(outcome);
    else active.reject(value instanceof Error ? value : new Error(String(value || 'IMA ad failed')));
  }

  function settleSlotFailure(detail, error) {
    const active = pending;
    if (!active) return;

    // STARTED or IMPRESSION consumes the slot. A later IMA lifecycle error must
    // never create a second ad inside the same explicit position.
    if (active.started) {
      emit({
        phase: 'complete',
        event: 'midroll-slot-consumed-after-start',
        code: String(detail?.code || error?.code || 'WATCHVIM_MIDROLL_CONSUMED_AFTER_START'),
        message: String(detail?.message || error?.message || 'Ad slot consumed after the creative started'),
      });
      settle('resolve', {
        started: true,
        completed: false,
        skipped: false,
        consumed: true,
        code: detail?.code || error?.code || null,
      });
      return;
    }

    const canAdvanceFixedMidroll = active.position === 'midroll'
      && (active.tagSource === 'google-demand' || active.tagSource === 'watchvim-house');
    if (canAdvanceFixedMidroll) {
      emit({
        phase: 'complete',
        event: 'midroll-slot-skipped',
        code: String(detail?.code || error?.code || 'WATCHVIM_MIDROLL_SLOT_SKIPPED'),
        message: String(detail?.message || error?.message || 'Midroll candidate unavailable; advancing within the fixed slot waterfall'),
      });
      settle('resolve', {
        started: false,
        completed: false,
        skipped: true,
        consumed: false,
        code: detail?.code || error?.code || null,
      });
      return;
    }
    settle('reject', error);
  }

  adsLoader.addEventListener(
    ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
    (event) => {
      const context = event.getUserRequestContext?.() || {};
      if (!pending || context.requestId !== pending.requestId) return;
      try {
        emit({ phase: 'loaded', event: 'ads-manager-loaded' });
        const settings = new ima.AdsRenderingSettings();
        // A three-slot midroll is one WatchVIM break. IMA must not restore the
        // movie after Slot 1 or Slot 2; PlaybackPage resumes once after the full
        // explicit pod resolves.
        settings.restoreCustomPlaybackStateOnAdBreakComplete = pending.position !== 'midroll';
        settings.enablePreloading = true;
        manager = event.getAdsManager(contentVideo || playhead, settings);

        manager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, (adErrorEvent) => {
          const detail = errorDetail(adErrorEvent);
          emit({ phase: 'error', event: 'error', ...detail });
          const error = Object.assign(new Error(detail.message), { code: detail.code });
          settleSlotFailure(detail, error);
        });
        manager.addEventListener(ima.AdEvent.Type.LOADED, (adEvent) => {
          const durationSec = Number(adEvent?.getAd?.()?.getDuration?.());
          if (!Number.isFinite(durationSec) || durationSec < MIN_LINEAR_SECONDS || durationSec > MAX_LINEAR_SECONDS) {
            const error = Object.assign(
              new Error(`Linear ad duration must be between ${MIN_LINEAR_SECONDS} and ${MAX_LINEAR_SECONDS} seconds`),
              { code: 'WATCHVIM_AD_DURATION_OUT_OF_RANGE', durationSec },
            );
            const detail = {
              code: error.code,
              message: error.message,
              durationSec,
            };
            emit({ phase: 'error', event: 'invalid-duration', ...detail });
            settleSlotFailure(detail, error);
            return;
          }
          if (pending) pending.durationSec = durationSec;
          emit({ phase: 'loaded', event: 'loaded', durationSec });
        });
        manager.addEventListener(ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
          try { pauseContent?.(); } catch (_) {}
          emit({ phase: 'playing', event: 'content-pause-requested' });
        });
        manager.addEventListener(ima.AdEvent.Type.IMPRESSION, () => {
          if (pending) {
            pending.started = true;
            pending.startPlaybackTimer();
          }
          emit({ phase: 'playing', event: 'impression' });
        });
        manager.addEventListener(ima.AdEvent.Type.STARTED, () => {
          if (pending) {
            pending.started = true;
            pending.startPlaybackTimer();
          }
          emit({ phase: 'playing', event: 'started' });
          startCountdown();
        });
        manager.addEventListener(ima.AdEvent.Type.COMPLETE, () => {
          if (pending) pending.completed = true;
          clearCountdown();
          emit({ phase: 'playing', event: 'progress', remainingSec: 0 });
          emit({ phase: 'playing', event: 'creative-complete' });
        });
        manager.addEventListener(ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
          if (pending?.position === 'midroll') {
            try { pauseContent?.(); } catch (_) {}
          }
          emit({ phase: 'complete', event: 'content-resume-requested' });
          settle('resolve', {
            started: pending?.started === true,
            completed: pending?.completed === true,
            skipped: pending?.started !== true,
            consumed: pending?.started === true,
          });
        });
        manager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
          emit({ phase: 'complete', event: 'all-ads-completed' });
          settle('resolve', {
            started: pending?.started === true,
            completed: pending?.completed === true,
            skipped: pending?.started !== true,
            consumed: pending?.started === true,
          });
        });

        const { width, height } = dimensions(container);
        manager.init(width, height, ima.ViewMode.NORMAL);
        try { manager.setVolume(pending.muted ? 0 : 1); } catch (_) {}
        manager.start();
      } catch (error) {
        emit({
          phase: 'error',
          event: 'manager-start-error',
          code: String(error?.code || 'WATCHVIM_IMA_MANAGER_START'),
          message: String(error?.message || 'IMA manager failed to start'),
        });
        settleSlotFailure({ code: error?.code, message: error?.message }, error);
      }
    },
  );

  adsLoader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, (event) => {
    const context = event.getUserRequestContext?.() || {};
    if (!pending || (context.requestId && context.requestId !== pending.requestId)) return;
    const detail = errorDetail(event);
    emit({ phase: 'error', event: 'error', ...detail });
    const error = Object.assign(new Error(detail.message), { code: detail.code });
    settleSlotFailure(detail, error);
  });

  function initialize() {
    if (destroyed || initialized) return;
    displayContainer.initialize();
    initialized = true;
    emit({ phase: 'ready', event: 'display-container-initialized', tagSource: 'watchvim-player', position: '', slotId: '' });
  }

  function resize() {
    if (!manager || destroyed) return;
    const { width, height } = dimensions(container);
    try { manager.resize(width, height, ima.ViewMode.NORMAL); } catch (_) {}
  }

  async function playTag(tagUrl, {
    muted = false,
    loadTimeoutMs = 15000,
    maxAdMs = 90000,
    requestBudgetMs = Infinity,
    contentDuration = 0,
    contentTitle = '',
    position = '',
    slotId = '',
    slotNumber = 0,
    slotTotal = 0,
  } = {}) {
    if (destroyed) throw new Error('IMA controller is destroyed');
    if (!initialized) throw new Error('IMA display container must be initialized by a viewer action');
    if (pending) throw new Error('An IMA ad request is already in progress');

    destroyManager();
    const requestId = ++requestSequence;
    const consentTag = videoTagWithConsent(tagUrl, getConsent());
    if (!consentTag) {
      return { attempted: false, started: false, completed: false, skipped: true, consumed: false, code: 'WATCHVIM_VIDEO_CONSENT_BLOCKED' };
    }
    const tag = withCachebuster(consentTag);
    if (!tag) throw new Error('Missing VAST tag');

    const tagSource = adTagSource(tag);
    if (tagSource === 'google-demand') {
      await publisherReady;
      if (destroyed) throw new Error('IMA controller is destroyed');
      // Consent may have changed while Google's library was loading.
      if (videoTagWithConsent(tagUrl, getConsent()) !== consentTag) {
        return { attempted: false, started: false, completed: false, skipped: true, consumed: false, code: 'WATCHVIM_VIDEO_CONSENT_CHANGED' };
      }
    }
    const adPosition = String(position || '').toLowerCase() === 'midroll' ? 'midroll' : 'preroll';
    const normalizedSlotId = String(slotId || '');
    const duplicateHouse = tagSource === 'watchvim-house'
      && lastHouseCompletion?.position === adPosition
      && lastHouseCompletion?.slotId === normalizedSlotId
      && Date.now() - Number(lastHouseCompletion?.at || 0) < 5000;
    if (duplicateHouse) {
      const error = Object.assign(new Error('Duplicate WatchVIM house fallback suppressed inside the same explicit slot'), {
        code: 'WATCHVIM_DUPLICATE_HOUSE_FALLBACK',
      });
      emit({
        phase: 'suppressed',
        event: 'duplicate-house-suppressed',
        code: error.code,
        message: error.message,
        tagSource,
        position: adPosition,
        slotId: normalizedSlotId,
        slotNumber,
        slotTotal,
      });
      throw error;
    }

    emit({
      phase: 'requesting',
      event: 'requesting',
      tagSource,
      position: adPosition,
      slotId: normalizedSlotId,
      slotNumber,
      slotTotal,
    });
    return new Promise((resolve, reject) => {
      const request = new ima.AdsRequest();
      request.adTagUrl = tag;
      const { width, height } = dimensions(container);
      request.linearAdSlotWidth = width;
      request.linearAdSlotHeight = height;
      request.nonLinearAdSlotWidth = width;
      request.nonLinearAdSlotHeight = Math.max(1, Math.round(height / 3));
      request.vastLoadTimeout = Math.max(3000, Math.min(65000, Number(loadTimeoutMs) || 15000));
      if (Number(contentDuration) > 0) request.contentDuration = Number(contentDuration);
      if (contentTitle) request.contentTitle = String(contentTitle);
      request.setAdWillAutoPlay(false);
      request.setAdWillPlayMuted(Boolean(muted));
      request.setContinuousPlayback(false);

      const creativeBudgetMs = Math.max(5000, Math.min(90000, Number(maxAdMs) || 90000));
      const onTimeout = (code, message) => {
        const detail = { code, message };
        emit({ phase: 'error', event: 'timeout', ...detail });
        settleSlotFailure(detail, Object.assign(new Error(message), { code }));
      };
      // Loading and creative playback have independent clocks. A slow VAST
      // response must not truncate a valid 90-second ad.
      let playbackTimerStarted = false;
      const startPlaybackTimer = () => {
        if (!pending || playbackTimerStarted) return;
        playbackTimerStarted = true;
        window.clearTimeout(pending.timer);
        pending.timer = window.setTimeout(() => onTimeout('WATCHVIM_AD_TIMEOUT', 'Ad playback timed out'), creativeBudgetMs + AD_TIMER_GRACE_MS);
      };
      const timer = window.setTimeout(() => onTimeout('WATCHVIM_AD_LOAD_TIMEOUT', 'Ad request timed out'), request.vastLoadTimeout);
      const deadlineTimer = Number.isFinite(requestBudgetMs)
        ? window.setTimeout(() => onTimeout('WATCHVIM_AD_BREAK_TIMEOUT', 'Ad break timed out'), Math.max(1, requestBudgetMs))
        : null;

      pending = {
        requestId,
        tagUrl: tag,
        tagSource,
        position: adPosition,
        slotId: normalizedSlotId,
        slotNumber: Math.max(0, Math.floor(Number(slotNumber) || 0)),
        slotTotal: Math.max(0, Math.floor(Number(slotTotal) || 0)),
        muted: Boolean(muted),
        timer,
        deadlineTimer,
        startPlaybackTimer,
        resolve,
        reject,
        settled: false,
        started: false,
        completed: false,
        durationSec: null,
      };
      try {
        adsLoader.requestAds(request, { requestId });
      } catch (error) {
        emit({
          phase: 'error',
          event: 'request-error',
          code: String(error?.code || 'WATCHVIM_IMA_REQUEST'),
          message: String(error?.message || 'IMA ad request failed'),
        });
        settleSlotFailure({ code: error?.code, message: error?.message }, error);
      }
    });
  }

  function contentComplete() {
    try { adsLoader.contentComplete(); } catch (_) {}
  }

  function destroy() {
    destroyed = true;
    clearCountdown();
    if (pending) settle('reject', new Error('IMA controller destroyed'));
    destroyManager();
    try { adsLoader.destroy?.(); } catch (_) {}
    try { displayContainer.destroy?.(); } catch (_) {}
    if (contentVideoIsBridge) {
      try { contentVideo.remove(); } catch (_) {}
    }
  }

  return { initialize, resize, playTag, contentComplete, destroy };
}

